/**
 * variant-gallery.js
 *
 * Fully self-contained gallery controller. Zero dependency on Dawn's
 * SliderComponent or any other theme JS.
 *
 * Features:
 *  - Renders gallery on page load for the initially selected variant
 *  - Swaps gallery + thumbnails on variant change
 *  - Main image slider driven by CSS transform (not scroll)
 *  - Thumbnail strip driven by CSS transform (not scroll — avoids left-overflow issues)
 *  - Thumbnail prev/next arrows with disabled state
 *  - Thumbnail centering when all fit; arrow-driven paging when they don't
 *  - Click main image → fullscreen zoom overlay
 *  - Zoom: click to toggle, scroll wheel, pinch, drag to pan
 */

// ─── Zoom Overlay ─────────────────────────────────────────────────────────────

class ZoomOverlay {
  constructor() {
    this._images = [];
    this._index  = 0;
    this.scale   = 1;
    this.ox = 0; this.oy = 0;
    this._dragging = false;
    this._didDrag  = false;
    this._lx = 0; this._ly = 0;
    this._pinchDist = 0;
    this._lastTap   = 0;
    this._touchMoved = false;
    this._build();
    this._bindEvents();
  }

  _build() {
    const s = document.createElement('style');
    s.textContent = `
      #vg-overlay { display:none; position:fixed; inset:0; z-index:100000;
        background:rgba(0,0,0,.92); align-items:center; justify-content:center; }
      #vg-overlay.open { display:flex; }
      #vg-ov-stage { position:relative; z-index:1; max-width:90vw; max-height:90vh;
        display:flex; align-items:center; justify-content:center; overflow:hidden; }
      #vg-ov-img { max-width:90vw; max-height:90vh; width:auto; height:auto;
        display:block; user-select:none; transform-origin:center;
        transition:transform .05s linear; cursor:zoom-in; }
      #vg-ov-close { position:absolute; top:14px; right:18px; z-index:2;
        background:rgba(255,255,255,.15); border:none; color:#fff; font-size:20px;
        width:38px; height:38px; border-radius:50%; cursor:pointer;
        display:flex; align-items:center; justify-content:center; }
      #vg-ov-close:hover { background:rgba(255,255,255,.3); }
      .vg-ov-nav { position:absolute; top:50%; transform:translateY(-50%); z-index:2;
        background:rgba(255,255,255,.15); border:none; color:#fff; font-size:38px;
        width:46px; height:62px; border-radius:4px; cursor:pointer;
        display:flex; align-items:center; justify-content:center; padding:0; }
      .vg-ov-nav:hover { background:rgba(255,255,255,.3); }
      .vg-ov-nav.hidden { display:none; }
      #vg-ov-prev { left:10px; }
      #vg-ov-next { right:10px; }
      #vg-ov-counter { position:absolute; bottom:14px; left:50%; transform:translateX(-50%);
        z-index:2; color:rgba(255,255,255,.7); font-size:13px; font-family:sans-serif; }
    `;
    document.head.appendChild(s);

    const el = document.createElement('div');
    el.id = 'vg-overlay';
    el.innerHTML = `
      <div id="vg-ov-backdrop"></div>
      <button id="vg-ov-close">&#x2715;</button>
      <button id="vg-ov-prev" class="vg-ov-nav hidden">&#8249;</button>
      <button id="vg-ov-next" class="vg-ov-nav hidden">&#8250;</button>
      <div id="vg-ov-stage"><img id="vg-ov-img" src="" alt="" draggable="false"></div>
      <div id="vg-ov-counter"></div>
    `;
    document.body.appendChild(el);

    this.el      = el;
    this.stage   = el.querySelector('#vg-ov-stage');
    this.img     = el.querySelector('#vg-ov-img');
    this.closeBtn= el.querySelector('#vg-ov-close');
    this.prevBtn = el.querySelector('#vg-ov-prev');
    this.nextBtn = el.querySelector('#vg-ov-next');
    this.counter = el.querySelector('#vg-ov-counter');
  }

  _bindEvents() {
    // Close when clicking outside stage (but not nav buttons)
    this.el.addEventListener('click', (e) => {
      if (this.stage.contains(e.target)) return;
      if (this.prevBtn.contains(e.target) || this.nextBtn.contains(e.target)) return;
      if (this.closeBtn.contains(e.target)) return;
      this.close();
    });
    this.closeBtn.addEventListener('click', () => this.close());
    this.prevBtn.addEventListener('click',  () => this._nav(-1));
    this.nextBtn.addEventListener('click',  () => this._nav(1));

    document.addEventListener('keydown', (e) => {
      if (!this.el.classList.contains('open')) return;
      if (e.key === 'Escape')     this.close();
      if (e.key === 'ArrowLeft')  this._nav(-1);
      if (e.key === 'ArrowRight') this._nav(1);
    });

    this.stage.addEventListener('wheel',      (e) => this._onWheel(e),      { passive: false });
    this.stage.addEventListener('mousedown',  (e) => this._onMD(e));
    window.addEventListener('mousemove',      (e) => this._onMM(e));
    window.addEventListener('mouseup',        ()  => this._onMU());
    this.stage.addEventListener('click',      (e) => this._onClick(e));
    this.stage.addEventListener('touchstart', (e) => this._onTS(e), { passive: false });
    this.stage.addEventListener('touchmove',  (e) => this._onTM(e), { passive: false });
    this.stage.addEventListener('touchend',   (e) => this._onTE(e));
  }

  open(images, index) {
    this._images = images;
    this._goto(index);
    this.el.classList.add('open');
    document.body.style.overflow = 'hidden';
  }

  close() {
    this.el.classList.remove('open');
    document.body.style.overflow = '';
    this._reset();
  }

  _nav(dir) {
    const n = this._index + dir;
    if (n >= 0 && n < this._images.length) this._goto(n);
  }

  _goto(i) {
    this._index = i;
    this._reset();
    this.img.src = this._images[i];
    this.counter.textContent = `${i + 1} / ${this._images.length}`;
    this.prevBtn.classList.toggle('hidden', i === 0);
    this.nextBtn.classList.toggle('hidden', i === this._images.length - 1);
  }

  _apply() {
    this.img.style.transform = `scale(${this.scale}) translate(${this.ox/this.scale}px,${this.oy/this.scale}px)`;
    this.img.style.cursor = this.scale > 1 ? 'grab' : 'zoom-in';
  }

  _clamp() {
    const r = this.img.getBoundingClientRect();
    const mx = Math.max(0, (r.width  * (this.scale - 1)) / 2);
    const my = Math.max(0, (r.height * (this.scale - 1)) / 2);
    this.ox = Math.min(mx, Math.max(-mx, this.ox));
    this.oy = Math.min(my, Math.max(-my, this.oy));
  }

  _reset() {
    this.scale = 1; this.ox = 0; this.oy = 0;
    this.img.style.transform = '';
    this.img.style.cursor = 'zoom-in';
  }

  _onWheel(e) {
    e.preventDefault();
    this.scale = Math.min(5, Math.max(1, this.scale + (e.deltaY < 0 ? 0.2 : -0.2)));
    this._clamp(); this._apply();
    if (this.scale === 1) this._reset();
  }

  _onMD(e) {
    if (this.scale <= 1) return;
    e.preventDefault();
    this._dragging = true; this._didDrag = false;
    this._lx = e.clientX; this._ly = e.clientY;
    this.img.style.cursor = 'grabbing';
  }

  _onMM(e) {
    if (!this._dragging) return;
    this._didDrag = true;
    this.ox += e.clientX - this._lx; this.oy += e.clientY - this._ly;
    this._lx = e.clientX; this._ly = e.clientY;
    this._clamp(); this._apply();
  }

  _onMU() {
    this._dragging = false;
    this.img.style.cursor = this.scale > 1 ? 'grab' : 'zoom-in';
  }

  _onClick(e) {
    if (this._didDrag) { this._didDrag = false; return; }
    this.scale > 1 ? this._reset() : (() => { this.scale = 2.5; this._clamp(); this._apply(); })();
  }

  _onTS(e) {
    if (e.touches.length === 2) {
      e.preventDefault();
      this._pinchDist = Math.hypot(e.touches[0].clientX - e.touches[1].clientX,
                                   e.touches[0].clientY - e.touches[1].clientY);
    } else {
      this._lx = e.touches[0].clientX; this._ly = e.touches[0].clientY;
      this._touchMoved = false; this._lastTap = Date.now();
    }
  }

  _onTM(e) {
    if (e.touches.length === 2) {
      e.preventDefault();
      const d = Math.hypot(e.touches[0].clientX - e.touches[1].clientX,
                           e.touches[0].clientY - e.touches[1].clientY);
      this.scale = Math.min(5, Math.max(1, this.scale * (d / this._pinchDist)));
      this._pinchDist = d; this._clamp(); this._apply();
    } else if (e.touches.length === 1 && this.scale > 1) {
      e.preventDefault();
      this._touchMoved = true;
      this.ox += e.touches[0].clientX - this._lx;
      this.oy += e.touches[0].clientY - this._ly;
      this._lx = e.touches[0].clientX; this._ly = e.touches[0].clientY;
      this._clamp(); this._apply();
    }
  }

  _onTE(e) {
    if (e.touches.length === 0 && !this._touchMoved && Date.now() - this._lastTap < 300) {
      this.scale > 1 ? this._reset() : (() => { this.scale = 2.5; this._clamp(); this._apply(); })();
    }
    if (e.touches.length === 0 && this.scale <= 1) this._reset();
  }
}

const zoomOverlay = new ZoomOverlay();


// ─── VariantGallery ───────────────────────────────────────────────────────────

class VariantGallery {
  constructor(sectionId) {
    this.sid = sectionId;

    // Main slider
    this.mainEl   = document.getElementById(`vg-main-${sectionId}`);
    this.slidesEl = document.getElementById(`vg-slides-${sectionId}`);

    // Thumbnail strip
    this.stripEl  = document.getElementById(`vg-thumb-strip-${sectionId}`);
    this.trackEl  = document.getElementById(`vg-thumb-track-${sectionId}`);
    this.prevBtn  = this.stripEl?.querySelector('.vg-thumb-prev');
    this.nextBtn  = this.stripEl?.querySelector('.vg-thumb-next');

    // Variant data
    const dataEl = document.getElementById(`VariantGalleryData-${sectionId}`);
    this.variantData = dataEl ? JSON.parse(dataEl.textContent) : {};

    this._currentImages = [];
    this._activeIndex   = 0;   // current main slide index
    this._thumbOffset   = 0;   // how many thumbs we've scrolled past
    this._thumbsPerPage = 4;   // how many thumbs are visible at once (recalculated)

    this._init();
  }

  // ── Bootstrap ──────────────────────────────────────────────────────────────

  _init() {
    // Render initial variant
    const variantInput = document.querySelector(
      `#MainProduct-${this.sid} input.product-variant-id,` +
      `#MainProduct-${this.sid} input[name="id"]`
    );
    if (variantInput?.value) {
      this._currentImages = this.variantData[variantInput.value]?.images || [];
      this._render(this._currentImages);
    }

    // Listen for variant changes
    const listen = (e) => {
      const id = e.detail?.variant?.id;
      if (!id) return;
      this._currentImages = this.variantData[id]?.images || [];
      this._render(this._currentImages);
    };
    document.getElementById(`MainProduct-${this.sid}`)?.addEventListener('variant:change', listen);
    document.addEventListener('variant:change', listen);

    // Arrow clicks
    this.prevBtn?.addEventListener('click', () => this._shiftThumbs(-1));
    this.nextBtn?.addEventListener('click', () => this._shiftThumbs(1));

    // Recalculate on resize: both thumb state and main slide position
    window.addEventListener('resize', () => {
      this._setMainSlide(this._activeIndex, false);
      this._updateThumbState();
    }, { passive: true });
  }

  // ── Render ─────────────────────────────────────────────────────────────────

  _render(images) {
    this._activeIndex  = 0;
    this._thumbOffset  = 0;
    this._buildSlides(images);
    this._buildThumbs(images);
    this._setMainSlide(0, false);
    // Wait a frame for layout before measuring thumb dimensions
    requestAnimationFrame(() => this._updateThumbState());
  }

  _title() {
    return document.querySelector('.product__title h1')?.textContent?.trim() || '';
  }

  _buildSlides(images) {
    if (!this.slidesEl) return;
    const title = this._title();
    this.slidesEl.innerHTML = '';
    images.forEach((url, i) => {
      const div = document.createElement('div');
      div.className = `vg-slide${i === 0 ? ' is-active' : ''}`;
      div.dataset.index = i;

      const img = document.createElement('img');
      img.src     = url;
      img.alt     = i === 0 ? title : `${title} – image ${i + 1}`;
      img.loading = i === 0 ? 'eager' : 'lazy';
      img.width   = 800;
      img.height  = 800;
      img.style.cssText = 'width:100%;height:auto;display:block;cursor:zoom-in;';
      img.addEventListener('click', () => zoomOverlay.open(this._currentImages, i));

      div.appendChild(img);
      this.slidesEl.appendChild(div);
    });
  }

  _buildThumbs(images) {
    if (!this.trackEl) return;
    const title = this._title();
    this.trackEl.innerHTML = '';
    images.forEach((url, i) => {
      const btn = document.createElement('button');
      btn.className = `vg-thumb${i === 0 ? ' is-active' : ''}`;
      btn.dataset.index = i;
      btn.setAttribute('aria-label', `View image ${i + 1}`);

      const img = document.createElement('img');
      img.src    = url;
      img.alt    = `${title} – thumbnail ${i + 1}`;
      img.loading = 'lazy';
      img.width   = 72;
      img.height  = 72;

      btn.appendChild(img);
      btn.addEventListener('click', () => {
        this._setMainSlide(i, true);
        this._setActiveThumb(i);
      });
      this.trackEl.appendChild(btn);
    });
  }

  // ── Main slide navigation ──────────────────────────────────────────────────

  _setMainSlide(index, animate) {
    if (!this.slidesEl || !this.mainEl) return;
    this._activeIndex = index;

    const slideWidth = this.mainEl.offsetWidth;
    this.slidesEl.style.transition = animate ? 'transform 0.35s ease' : 'none';
    this.slidesEl.style.transform  = `translateX(-${index * slideWidth}px)`;

    this.slidesEl.querySelectorAll('.vg-slide').forEach((s, i) =>
      s.classList.toggle('is-active', i === index)
    );

    this._setActiveThumb(index);
  }

  // ── Thumbnail state ────────────────────────────────────────────────────────

  _setActiveThumb(index) {
    if (!this.trackEl) return;
    this.trackEl.querySelectorAll('.vg-thumb').forEach((b, i) =>
      b.classList.toggle('is-active', i === index)
    );
  }

  _updateThumbState() {
    if (!this.trackEl || !this.stripEl) return;

    const thumbs    = this.trackEl.querySelectorAll('.vg-thumb');
    const count     = thumbs.length;
    if (count === 0) return;

    const wrapWidth = this.trackEl.parentElement.offsetWidth;  // vg-thumb-track-wrap width
    const thumbW    = thumbs[0].offsetWidth + 8; // 72px + 8px gap
    this._thumbsPerPage = Math.max(1, Math.floor(wrapWidth / thumbW));

    // If all thumbs fit, hide arrows and center the track
    const allFit = count <= this._thumbsPerPage;
    if (this.prevBtn) this.prevBtn.disabled = true;
    if (this.nextBtn) this.nextBtn.disabled = allFit || this._thumbOffset >= count - this._thumbsPerPage;

    // Center when all fit by adding left padding; left-align otherwise
    if (allFit) {
      const totalWidth = count * thumbW - 8;
      const padding    = Math.max(0, (wrapWidth - totalWidth) / 2);
      this.trackEl.style.paddingLeft = `${padding}px`;
    } else {
      this.trackEl.style.paddingLeft = '0';
    }

    this._applyThumbTranslate();
    this._updateArrows();
  }

  _shiftThumbs(dir) {
    const thumbs = this.trackEl?.querySelectorAll('.vg-thumb');
    if (!thumbs) return;
    const count   = thumbs.length;
    const maxOffset = count - this._thumbsPerPage;
    this._thumbOffset = Math.min(maxOffset, Math.max(0, this._thumbOffset + dir));
    this._applyThumbTranslate();
    this._updateArrows();
  }

  _applyThumbTranslate() {
    if (!this.trackEl) return;
    const thumbs = this.trackEl.querySelectorAll('.vg-thumb');
    if (!thumbs.length) return;
    const thumbW = thumbs[0].offsetWidth + 8;
    this.trackEl.style.transform = `translateX(-${this._thumbOffset * thumbW}px)`;
  }

  _updateArrows() {
    if (!this.trackEl || !this.prevBtn || !this.nextBtn) return;
    const count     = this.trackEl.querySelectorAll('.vg-thumb').length;
    const maxOffset = count - this._thumbsPerPage;
    this.prevBtn.disabled = this._thumbOffset <= 0;
    this.nextBtn.disabled = this._thumbOffset >= maxOffset;
  }
}

// ─── Boot ──────────────────────────────────────────────────────────────────────

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('[data-section][data-product-id]').forEach((el) => {
    const sectionId = el.dataset.section;
    if (sectionId) new VariantGallery(sectionId);
  });
});
